import { useCallback, useEffect, useRef, useState } from "react";

import {
  fetchRoomToken,
  relayWsUrl,
  type TopicParticipant,
} from "@/lib/api";

export interface TranscriptSegment {
  seq: number;
  speaker: string | null;
  text: string;
  /** "speech" (spoken) or "system" (join/leave marker, rendered inline, excluded from AI). */
  kind?: string;
  startedAtMs?: number;
}

export type SignalType =
  | "decision"
  | "risk"
  | "open_question"
  | "action_item"
  | "topic_shift"
  | "diagram_request";

export interface AnalysisSignal {
  /** Unique key for React rendering (type + summary hash). */
  id: string;
  type: SignalType;
  summary: string;
  confidence: number;
}

export type RoomStatus = "connecting" | "open" | "closed" | "error";

interface RelayMessage {
  event?: string;
  [key: string]: unknown;
}

// Speaker may arrive as a plain string (mock path) or, from the real LiveKit path, as a
// participant object ({ name, participantId, livekitIdentity, … }). Always reduce it to a
// display string so it can never render as an invalid React child.
function speakerToString(s: unknown): string | null {
  if (s == null) return null;
  if (typeof s === "string") return s || null;
  if (typeof s === "object") {
    const o = s as Record<string, unknown>;
    return (
      (o.name as string) ||
      (o.participantId as string) ||
      (o.livekitIdentity as string) ||
      null
    );
  }
  return String(s);
}

function normalizeSeg(m: Record<string, unknown>): TranscriptSegment {
  return {
    seq: Number(m.seq),
    speaker: speakerToString(m.speaker),
    text: String(m.text ?? ""),
    kind: typeof m.kind === "string" ? m.kind : "speech",
    startedAtMs: m.startedAtMs as number | undefined,
  };
}

/**
 * Connects to the relay WebSocket for a room: fetches a bridge token, opens `/ws`, and
 * tracks the live transcript, the latest interim line, the most recent analysis-window
 * summary, and presence. Exposes forceAnalysis() (asks the server to cut a window now) and
 * reconnect(). Cleans up on unmount / roomId change.
 */
export function useRoomChannel(roomId: string | undefined) {
  const [status, setStatus] = useState<RoomStatus>("connecting");
  const [segments, setSegments] = useState<TranscriptSegment[]>([]);
  const [interim, setInterim] = useState("");
  const [windowSummary, setWindowSummary] = useState("");
  const [signals, setSignals] = useState<AnalysisSignal[]>([]);
  const [participants, setParticipants] = useState<TopicParticipant[]>([]);
  // Bumped whenever the server signals the subtopic checklist changed (human edit or AI
  // check-off). Consumers refetch on change rather than carrying the list through the relay.
  const [subtopicsNonce, setSubtopicsNonce] = useState(0);
  // Rolling-summary-derived AI state (pushed via the `ai_update` event).
  const [facilitatorQuestion, setFacilitatorQuestion] = useState<string | null>(null);
  const [suggestedNextTopic, setSuggestedNextTopic] = useState<string | null>(null);
  const [observations, setObservations] = useState<AnalysisSignal[]>([]);
  // Voice-agent status: 'listening' (heard Hey Max, waiting for a pause) | 'responding' |
  // 'greeting' | null (idle). Drives a small Max status line in the room.
  const [maxState, setMaxState] = useState<string | null>(null);
  // Deep-research Max: bumped whenever a research job starts/completes so ResearchPanel
  // refetches; researchInProgress carries the in-flight jobs for the "Researching…" chip.
  const [researchNonce, setResearchNonce] = useState(0);
  const [researchInProgress, setResearchInProgress] = useState<
    { jobId: number; question: string }[]
  >([]);
  const wsRef = useRef<WebSocket | null>(null);
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    if (!roomId) return;
    let cancelled = false;
    let ws: WebSocket | null = null;
    setStatus("connecting");

    (async () => {
      try {
        const token = await fetchRoomToken(roomId);
        if (cancelled) return;
        ws = new WebSocket(relayWsUrl(token));
        wsRef.current = ws;
        ws.onopen = () => !cancelled && setStatus("open");
        ws.onerror = () => !cancelled && setStatus("error");
        ws.onclose = () => !cancelled && setStatus("closed");
        ws.onmessage = (e) => {
          if (cancelled) return;
          let msg: RelayMessage;
          try {
            msg = JSON.parse(String(e.data));
          } catch {
            return;
          }
          switch (msg.event) {
            case "room_snapshot": {
              const snap = msg.snapshot as Record<string, unknown> | null;
              const recent = (snap?.recentSegments as Record<string, unknown>[]) ?? [];
              setSegments(recent.map(normalizeSeg));
              setParticipants((snap?.participants as TopicParticipant[]) ?? []);
              break;
            }
            case "transcript_segment":
              setInterim("");
              setSegments((prev) => {
                const seg = normalizeSeg(msg);
                return prev.some((s) => s.seq === seg.seq) ? prev : [...prev, seg];
              });
              break;
            case "transcript_interim":
              setInterim(String(msg.text ?? ""));
              break;
            case "analysis_window_enriched": {
              if (msg.summary) setWindowSummary(String(msg.summary));
              const raw = (msg.signals as Record<string, unknown>[] | undefined) ?? [];
              if (raw.length) {
                const incoming: AnalysisSignal[] = raw.map((s, i) => ({
                  id: `${msg.windowSeq ?? Date.now()}-${i}`,
                  type: (s.type as SignalType) ?? "decision",
                  summary: String(s.summary ?? ""),
                  confidence: Number(s.confidence ?? 0),
                }));
                setSignals((prev) => [...prev, ...incoming].slice(-24));
              }
              break;
            }
            // A participant joined/left: refresh the roster live for everyone already in the
            // room (previously only the initial room_snapshot carried the list, so existing
            // clients never saw new joiners' names).
            case "participants_changed": {
              setParticipants((msg.participants as TopicParticipant[]) ?? []);
              break;
            }
            case "room_updated": {
              // room_updated spreads the snapshot at the top level (unlike room_snapshot,
              // which nests it under `snapshot`), so read participants from either shape.
              const parts = (msg.participants ??
                (msg.snapshot as Record<string, unknown> | undefined)?.participants) as
                | TopicParticipant[]
                | undefined;
              if (parts) setParticipants(parts);
              break;
            }
            case "subtopics_updated":
              setSubtopicsNonce((n) => n + 1);
              break;
            case "research_started": {
              const jobId = Number(msg.jobId);
              const question = String(msg.question ?? "");
              setResearchInProgress((prev) =>
                prev.some((r) => r.jobId === jobId) ? prev : [...prev, { jobId, question }],
              );
              setResearchNonce((n) => n + 1);
              break;
            }
            case "research_done": {
              const jobId = Number(msg.jobId);
              setResearchInProgress((prev) => prev.filter((r) => r.jobId !== jobId));
              setResearchNonce((n) => n + 1);
              break;
            }
            case "max_state": {
              const s = typeof msg.state === "string" ? msg.state : "idle";
              setMaxState(s === "idle" ? null : s);
              break;
            }
            case "ai_update": {
              if (typeof msg.facilitatorQuestion === "string")
                setFacilitatorQuestion(msg.facilitatorQuestion);
              if (typeof msg.suggestedNextTopic === "string")
                setSuggestedNextTopic(msg.suggestedNextTopic);
              if (typeof msg.summaryMd === "string" && msg.summaryMd)
                setWindowSummary(String(msg.summaryMd));
              if (Array.isArray(msg.observations)) {
                const obs = (msg.observations as Record<string, unknown>[]).map((o, i) => ({
                  id: `obs-${i}`,
                  type: (o.type as SignalType) ?? "decision",
                  summary: String(o.summary ?? ""),
                  confidence: 1,
                }));
                setObservations(obs);
              }
              break;
            }
            default:
              break;
          }
        };
      } catch {
        if (!cancelled) setStatus("error");
      }
    })();

    return () => {
      cancelled = true;
      try {
        ws?.close();
      } catch {
        /* ignore */
      }
      wsRef.current = null;
    };
  }, [roomId, nonce]);

  const forceAnalysis = useCallback(() => {
    const ws = wsRef.current;
    if (ws && ws.readyState === ws.OPEN) {
      ws.send(JSON.stringify({ type: "analysis.force_window" }));
    }
  }, []);

  const reconnect = useCallback(() => setNonce((n) => n + 1), []);

  return {
    status,
    segments,
    interim,
    windowSummary,
    signals,
    participants,
    subtopicsNonce,
    facilitatorQuestion,
    suggestedNextTopic,
    observations,
    maxState,
    researchNonce,
    researchInProgress,
    forceAnalysis,
    reconnect,
  };
}
